export default function WorldEditor() {
  return (
    <section id="world-editor">
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">World Editor</h2>
      <p className="text-slate-600 dark:text-slate-400 mb-8 leading-relaxed">
        The World Editor gives you tools to modify the contents of your world database — entities,
        portals, maps, chunks, villager trades, saved structures, and the Ender Dragon fight. Each
        tool is in its own sub-tab.
      </p>

      {/* Pruning */}
      <div id="world-pruning" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Pruning</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-4 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Remove chunks from your world to reduce file size or regenerate terrain. Useful when a
            world has grown very large and you want to trim distant areas you no longer need.
          </p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">World Map</h4>
            <p>
              Select a dimension (Overworld, Nether, or The End) and click <strong>Load</strong> to
              render a visual map of the dimension. The area around spawn appears within seconds so
              you can start working immediately, with the rest filling in progressively. Rendered maps
              are cached, so reopening the same world is nearly instant.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Y-Level Selector</h4>
            <p>
              Choose which Y level to render the map at. This is useful for viewing underground
              caves, ancient cities, or specific layers of your world. The Nether defaults to Y=100
              so you can see fortresses and structures instead of the netherrack ceiling.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Selecting Chunks</h4>
            <p className="mb-2">
              Use the <strong>Rectangle</strong> or <strong>Circle</strong> tool to click and drag on
              the world map to make selections. By default, any area you highlight will be marked as
              chunks to <em>keep</em>. Click the <strong>Inverse</strong> button to flip the
              selection so highlighted areas are pruned instead.
            </p>
            <p>
              You can draw as many shapes as you like to precisely define which chunks to keep or
              remove. The info box below the selection tools shows how many chunks are selected and
              how many will be pruned. Hold the <strong>middle mouse button</strong> to pan the map
              at any time — this works even while a selection tool is active, so you don't need to
              switch back to the pan tool.
            </p>
            <p>
              Alternatively, type exact block coordinates into
              the <strong>X1</strong>, <strong>Z1</strong>, <strong>X2</strong>, and <strong>Z2</strong> fields
              below the selection tools and click <strong>Add Rectangle</strong> to place a precise
              rectangle on the map. This is useful when you know the exact coordinates of an area you
              want to keep or remove.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Executing</h4>
            <p>
              Click <strong>Execute Pruning</strong> to begin. A backup is created automatically
              before pruning starts. Unlike most other changes in the program, pruning is applied
              immediately — it is not staged for a later save.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Reset Dimension</h4>
            <p>
              Completely wipe all chunk data from a dimension. The terrain will regenerate from the
              seed the next time you visit those areas in Minecraft. Like pruning, this is applied
              immediately after confirmation.
            </p>
          </div>

          <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-3">
            <p className="text-red-700 dark:text-red-400 text-xs">
              Pruning and dimension resets permanently delete chunk data. Builds and modifications
              in deleted chunks are lost forever. A backup is created automatically, but make sure
              you're happy with your selection before proceeding.
            </p>
          </div>
        </div>
      </div>

      {/* Chunk Inspector */}
      <div id="world-chunk-inspector" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Chunk Inspector</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Examine any individual chunk in detail. Right-click a chunk on the Pruning Tab map to
            jump straight to the inspector, or navigate to it manually.
          </p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Chunk Overview</strong> — View chunk metadata including version, finalization state, heightmap range, and subchunk count.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Block Breakdown</strong> — See every block type in the chunk with exact counts.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Block Entities & Entities</strong> — Browse tile entities (chests, signs, etc.) and mobs in the chunk with full NBT editing.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Structures & Biomes</strong> — View structure bounding boxes and biome distribution within the chunk.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Visual Preview</strong> — A rendered top-down image of the chunk showing actual block colours. Hover over any position to see the block at that location.</li>
          </ul>
          <p>
            The Y-Level Selector is also available in the Chunk Inspector, letting you view any
            vertical slice of the chunk.
          </p>
        </div>
      </div>

      {/* Entities */}
      <div id="world-entities" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Entities</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Browse and manage all entities in your world — mobs, dropped items, armour stands,
            minecarts, and anything else that exists as an entity in the game.
          </p>
          <p>
            Entities are loaded on demand (click the Load button) since large worlds can have thousands.
            Once loaded, you get a three-pane interface:
          </p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Entity Type List</strong> (left) — All entity types found in the world, with counts and icons. Use the search box to filter, and the dimension dropdown to show entities from a specific dimension.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Individual Entities</strong> (top right) — When you select a type, all entities of that type are listed with their position and name.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Entity Details</strong> (bottom right) — Select an entity to see its summary (type, name, position, health, dimension, tags) and a larger icon. A second tab shows the raw NBT data where you can edit individual values.</li>
          </ul>
          <p>
            You can delete individual entities or all entities of a specific type. Deletions are
            staged — they're visually marked but not applied until you save.
          </p>
        </div>
      </div>

      {/* Properties */}
      <div id="world-properties" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Properties</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            View and manage world-level dynamic properties and scoreboard data.
          </p>
          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Dynamic Properties</h4>
            <p>
              Shows all world dynamic properties set by add-ons and behaviour packs. You can view,
              edit, or remove individual properties. Use the search bar at the top to filter
              properties by key or value — the filter is case-insensitive and updates in real time
              as you type.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Scoreboards</h4>
            <p>
              Lists all scoreboard objectives and their scores. You can remove objectives or
              individual scores, and change score values.
            </p>
          </div>
        </div>
      </div>

      {/* Villager Trades */}
      <div id="world-villager-trades" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Villager Trades</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            View and edit trade offers for every villager in your world. Villagers are loaded on
            demand — click <strong>Load Villagers</strong> to scan the world.
          </p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Villager List</h4>
            <p>
              The left panel lists all villagers found in the world, showing their name (or
              "Unnamed Villager"), profession, and coordinates. Use the search box to filter by
              name or profession, and the dimension dropdown to show villagers from a specific
              dimension. Below the list you can change a villager's profession, career level, and
              biome variant. The <strong>Biome Type</strong> dropdown lets you switch a villager's
              appearance between Plains, Desert, Jungle, Savanna, Snow, Swamp, and Taiga — this
              changes how the villager looks in-game to match the selected biome.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Trade Editor</h4>
            <p>
              Select a villager to see all their trades in the right panel. Trades are grouped by
              career tier (Novice, Apprentice, Journeyman, Expert, Master) with visual dividers
              so you can see which trades belong to each level. Each trade shows:
            </p>
            <ul className="space-y-1 mt-2">
              <li>The <strong>buy item</strong> and quantity the villager wants</li>
              <li>The <strong>sell item</strong> and quantity the villager gives</li>
              <li>The <strong>max uses</strong> before the trade locks until the villager restocks (set to <code>-1</code> for unlimited uses)</li>
            </ul>
            <p className="mt-2">
              Click any item slot to open the full item editor — you can change the item type, set
              a custom name, add enchantments, apply armor trims, and toggle the unbreakable flag,
              just like editing items in a player's inventory.
            </p>
          </div>
        </div>
      </div>

      {/* Portals */}
      <div id="world-portals" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Portals</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            View all Nether portals in your world, including their positions, linked portals, and
            whether they're orphaned (missing their partner portal).
          </p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Statistics</strong> — Total portal count, Overworld vs. Nether counts, linked pairs, and orphaned portals.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Filters</strong> — Filter by dimension or by status (linked / orphaned).</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Portal List</strong> — Shows each portal's dimension, coordinates, linked destination, and status.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Delete Orphaned</strong> — Bulk-delete all orphaned portals. Useful for cleaning up portal data after pruning or resetting a dimension.</li>
          </ul>
          <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs">
              Deleting a portal removes the portal <em>data</em>, not the physical obsidian frame in
              your world. The next time you step into the portal in Minecraft, new portal data will
              be created and it may or may not link to the same destination as before.
            </p>
          </div>
        </div>
      </div>

      {/* Dragon Fight */}
      <div id="world-dragon" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Dragon Fight</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Manage the Ender Dragon fight state in The End dimension.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Dragon Status</h4>
              <ul className="space-y-1">
                <li>View current dragon fight state</li>
                <li>Check if the dragon has been defeated</li>
                <li>Respawn the Ender Dragon by resetting the fight state</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">End Gateways</h4>
              <ul className="space-y-1">
                <li>Scan for End gateway portals in the world</li>
                <li>View positions of discovered gateways</li>
                <li>Remove gateways — this deletes both the gateway data and the physical blocks from the world</li>
              </ul>
              <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 p-3 mt-3">
                <p className="text-red-700 dark:text-red-400 text-xs">
                  Removing End gateways is a destructive change that is applied immediately. A backup
                  is created automatically before the removal begins.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Maps */}
      <div id="world-maps" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Maps</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Browse all in-game map items stored in the world. Maps are loaded on demand for
            performance reasons.
          </p>
          <p>
            In Minecraft Bedrock, every time a map is created, 5 additional copies are also stored —
            one for each zoom level. This data is never cleaned up, even if the map item is lost or
            destroyed in-game. Over time, maps can seriously bloat a world's file size. This tool
            lets you see exactly what's there and remove what you don't need.
          </p>
          <ul className="space-y-2">
            <li><strong className="text-slate-800 dark:text-slate-200">Map List</strong> — Shows all maps with their IDs. Select one to see a preview of the map image.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Image Preview</strong> — Displays the actual map image as rendered by the game.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Import Image</strong> — Select any map and import a PNG or JPG image onto it. A preview dialog shows exactly how the image will look at 128×128 before you confirm. The map is automatically locked afterwards so your image stays put in-game.</li>
            <li><strong className="text-slate-800 dark:text-slate-200">Delete</strong> — Remove individual maps or all maps at once. Useful if you have hundreds of unwanted map items.</li>
          </ul>
        </div>
      </div>

      {/* Structures */}
      <div id="world-structures" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Structures</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            Browse every structure that has been saved in your world with a structure block. Each
            structure is a self-contained snapshot of blocks (and any entities inside them) that
            Minecraft stores against a namespaced name like <code>mystructure:house</code>.
            Structures are loaded on demand — click <strong>Load Structure Data</strong> to scan
            the world.
          </p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Structure List</h4>
            <p>
              The left panel lists every saved structure, showing the namespaced name and its
              dimensions (X×Y×Z). A summary line above the list shows the total count and combined
              data size. Use the search box to filter by namespace or name, and the
              <strong> Namespace</strong> dropdown to narrow the list to a single namespace
              (structure blocks default to <code>mystructure</code> unless you change it in-game).
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Details Panel</h4>
            <p>
              Selecting a structure shows its full details on the right: namespace and name, size
              and total block count, the world origin where it was originally saved, the structure
              format version, raw data size, palette entry count, block entity count (chests, signs,
              furnaces, etc.), entity count, and a preview of up to 16 block names from its palette.
              If the NBT failed to parse for any reason, the error is shown here too.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Export, Import, and Delete</h4>
            <ul className="space-y-2">
              <li><strong className="text-slate-800 dark:text-slate-200">Export to .mcstructure</strong> — Saves the selected structure to a <code>.mcstructure</code> file (named <code>namespace__name.mcstructure</code>) that you can drop into another world or share with someone else. The file format is identical to the one Minecraft itself produces.</li>
              <li><strong className="text-slate-800 dark:text-slate-200">Import .mcstructure...</strong> — Pick a <code>.mcstructure</code> file from disk and stage it as a new structure in this world. You'll be prompted for a namespace and name; if a structure with that name already exists, you'll be asked to confirm an overwrite. Pending imports appear in the list with green styling and a "(Pending Import)" label.</li>
              <li><strong className="text-slate-800 dark:text-slate-200">Delete Structure</strong> — Marks the selected structure for deletion (shown with strike-through styling in the list). The button switches to <strong>Restore Structure</strong> while it's staged, so you can undo before saving. For pending imports the button becomes <strong>Cancel Import</strong> and discards the staged file.</li>
            </ul>
            <p className="mt-3">
              Like other staging-based tabs, none of these changes touch the world until you click
              <strong> Save Changes</strong> in the footer.
            </p>
          </div>
        </div>
      </div>

      {/* Tick Data */}
      <div id="world-tick-data" className="mb-8">
        <h3 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">Tick Data</h3>
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5 space-y-3 text-sm text-slate-600 dark:text-slate-400">
          <p>
            View and manage tick-related data stored in the world database. Tick data is loaded on
            demand — click <strong>Load</strong> to scan the world.
          </p>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Ticking Areas</h4>
            <p>
              View and edit ticking areas created with the <code>/tickingarea</code> command. A
              side-by-side layout shows the list on the left and edit controls on the right.
              Supports both circle (center + radius) and rectangle (two corners) shapes. You can
              rename areas, change their shape and coordinates, or delete them.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Pending Ticks</h4>
            <p>
              Browse scheduled block updates per chunk — things like flowing water, lava, and
              redstone. Sortable columns show tick counts and the top block types in each chunk.
              Filter by dimension, and bulk-delete or restore with one click.
            </p>
          </div>

          <div>
            <h4 className="font-medium text-slate-800 dark:text-slate-200 mb-2">Random Ticks</h4>
            <p>
              Same interface as Pending Ticks, but for random tick queues — fire spread, crop growth,
              kelp, and similar block updates that happen on a random schedule.
            </p>
          </div>

          <div className="rounded-lg border border-blue-200 dark:border-blue-800/50 bg-blue-50 dark:bg-blue-900/20 p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs">
              The Chunk Inspector also shows a Tick Data section listing all pending and random tick
              entries for the loaded chunk, with block name, position, and tick time.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
